'use client'

import { useHtmlElementEvent } from '@/hooks/browser/useHtmlElementEvent'
import { ReactNode, RefObject, createContext, useContext, useMemo, useState } from 'react'

export type ScrollPosition = {
  scrollLeft: number
  scrollTop: number
}

type ScrollContextValue = {
  scrollingElementRef: RefObject<HTMLElement>
  scrollPosition: ScrollPosition
}

const ScrollContext = createContext<ScrollContextValue>({
  scrollingElementRef: { current: null },
  scrollPosition: {
    scrollLeft: 0,
    scrollTop: 0,
  },
})

export type ScrollContextProviderProps = {
  scrollingElementRef: RefObject<HTMLElement>
  children?: ReactNode
}

export function ScrollContextProvider({
  scrollingElementRef,
  children,
}: ScrollContextProviderProps) {
  const [scrollPosition, setScrollPosition] = useState({
    scrollLeft: scrollingElementRef?.current?.scrollLeft ?? 0,
    scrollTop: scrollingElementRef?.current?.scrollTop ?? 0,
  })
  useHtmlElementEvent('scroll', scrollingElementRef, () => {
    setScrollPosition({
      scrollLeft: scrollingElementRef?.current?.scrollLeft ?? 0,
      scrollTop: scrollingElementRef?.current?.scrollTop ?? 0,
    })
  })
  return (
    <ScrollContext.Provider
      value={useMemo(
        () => ({ scrollingElementRef, scrollPosition }),
        [scrollingElementRef, scrollPosition]
      )}
    >
      {children}
    </ScrollContext.Provider>
  )
}

export function useScrollPosition(): ScrollPosition {
  const contextValue = useContext(ScrollContext)
  return contextValue.scrollPosition
}
